import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { DataItem } from '@app/components/shared/model/data-item';
import { KombinationstypEnum } from '@app/constants/kombinationstyp';
import { PostRetryService } from '@app/services/post-retry/post-retry.service';
import { v4 as uuid } from 'uuid';
import { ChipListComponent } from '@app/components/shared/chip-list/chip-list.component';
import { Rider } from '@app/rest-client/faxe/models/rider';
import { RiderEventType } from '@app/constants/rider-event-type';
import { filter, takeUntil } from 'rxjs/operators';
import { FenceResult } from '@app/rest-client/faxe/models/fence-result';
import { RiderView } from '@app/facade-services/rider-view/model/rider-view';
import { MatCard } from '@angular/material/card';
import { MatButton } from '@angular/material/button';
import {
  defined,
  isEmpty,
  isNotEmpty,
} from '@app/fremework/functions';
import {
  getRiderLastFenceSequence,
  raceDuration,
  raceDurationOnFinish,
} from '@app/domain/functions';

import { PageLayoutComponent } from '@app/components/shared/page-layout/page-layout.component';
import { PageTitleComponent } from '@app/components/common/page-title/page-title.component';
import { ClubbLoggoService } from '@app/services/clubb-loggo.service';
import { RiderEventPost } from '@app/rest-client/faxe/models/rider-event-post';
import { UserToken } from '@app/services/password/model/user-token';
import { RaceViewService } from '@app/facade-services/race-view/race-view.service';
import { RollingHistListComponent } from '@app/components/shared/rolling-hist-list/rolling-hist-list.component';
import { PasswordService } from '@app/services/password/password.service';
import { RollingHistListService } from '@app/components/shared/rolling-hist-list/service/rolling-hist-list.service';
import { ListItem } from '@app/components/shared/rolling-hist-list/model/list-item';
import { MatSliderModule } from '@angular/material/slider';

@Component({
  selector: 'faxe-finish',
  templateUrl: './finish.component.html',
  imports: [
    ReactiveFormsModule,
    MatCard,
    MatButton,
    ChipListComponent,
    PageLayoutComponent,
    PageTitleComponent,
    RollingHistListComponent,
    FormsModule,
    MatSliderModule,
  ],
  styleUrls: ['./finish.component.scss'],
})
export class FinishComponent implements OnInit, OnDestroy {
  @ViewChild('riderComponent') riderComponent: ChipListComponent<
    DataItem<Rider>
  >;
  formGroup: UntypedFormGroup = this.formBuilder.group({});
  riderViewDataItems: DataItem<RiderView>[];
  finish: Date;
  stopTime: string;
  totalTime: string;
  selectedRider: RiderView;
  histListBucketId = 'finish';
  listItems: ListItem<any>[] = [];
  private unsubscribe$ = new Subject<void>();
  userToken: UserToken;
  value = 5000;

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly postRetryService: PostRetryService,
    private readonly raceViewService: RaceViewService,
    private readonly clubbLoggoService: ClubbLoggoService,
    private readonly passwordService: PasswordService,
    private readonly rollingHistListService: RollingHistListService
  ) {}

  ngOnInit(): void {
    this.passwordService
      .raceUserToken()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((token) => {
        this.userToken = token;
        this.fetchRidersOnTrack();
      });

    this.rollingHistListService.histListMap$
      .pipe(
        takeUntil(this.unsubscribe$),
        filter((h) => h?.has(this.histListBucketId))
      )
      .subscribe((histListMap) => {
        this.listItems = histListMap.get(this.histListBucketId);
      });
  }

  logoSrc(riderClubName: string | undefined) {
    return this.clubbLoggoService.logoSrc(riderClubName);
  }

  fetchRidersOnTrack(): void {
    if (this.selectedRider) {
      return;
    }

    this.raceViewService
      .selectRaceView(false, true, false, 'FinishComponent')
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((raceView) => {
        this.riderViewDataItems = raceView.riders
          ?.filter((riderView) => riderView.isOnTrack)
          ?.map((riderView) => ({
            key: riderView?.horseCombinationNumber,
            displayname: riderView?.horseCombinationNumber?.toString(),
            value: riderView,
            sortOrder: getRiderLastFenceSequence(riderView),
            kombinationstypEnum: KombinationstypEnum.must1of1,
            badge: this.mapBadge(riderView.fenceResults),
          }))
          ?.sort((a, b) => b.sortOrder - a.sortOrder);

        if (isEmpty(this.riderViewDataItems)) {
          this.clear();
        }

        if (isNotEmpty(this.riderViewDataItems)) {
          this.riderViewDataItems[0].glow = true;
        }
      });
  }

  mapBadge(fenceResults: FenceResult[]): string {
    const sorted = fenceResults
      .slice()
      ?.sort((a, b) => b.sequence - a.sequence);

    if (isNotEmpty(sorted)) {
      return sorted[0].fenceName;
    }
    return undefined;
  }

  finishRiderEventPost = (date: Date): RiderEventPost => ({
    eventTimestamp: date?.toISOString(),
    eventType: RiderEventType.finished,
    raceGuid: this.userToken.selectedRaceGuid,
  });

  formatLabel(value: number): string {
    const diff = Math.round((value - 5000) / 250);
    if (diff === 0) {
      return '0s';
    }
    return diff > 0 ? `+${diff}s` : `${diff}s`;
  }

  onFinish() {
    this.finish = new Date();
    this.recalculateFinishDisplay();
  }

  recalculateFinishDisplay() {
    if (
      this.finish &&
      this.selectedRider &&
      raceDurationOnFinish(this.selectedRider, this.finish)
    ) {
      this.totalTime = `Totaltid:  ${raceDurationOnFinish(
        this.selectedRider,
        this.finish
      )}`;
    } else {
      this.totalTime = undefined;
    }

    if (this.finish) {
      this.stopTime = `Stopptid:  ${this.finish?.toLocaleTimeString()}`;
    } else {
      this.stopTime = undefined;
    }
  }

  onSelectedRiderView($event: DataItem<RiderView>) {
    this.selectedRider = this.checkCreateGhostRider($event);
    this.recalculateFinishDisplay();
  }

  checkCreateGhostRider(dataItem: DataItem<RiderView>): RiderView {
    const selectedRider = dataItem.value
      ? dataItem.value
      : {
          raceClassGuid: undefined,
          riderGuid: undefined,
          horseCombinationNumber: +dataItem.displayname,
          fenceResults: [],
          riderEvents: [],
        };

    const selectedItem = this.riderViewDataItems.find(
      (r) => +r.displayname === selectedRider.horseCombinationNumber
    );

    if (!defined(selectedItem.value)) {
      selectedItem.value = selectedRider;
    }

    return selectedRider;
  }

  onRiderLastUnSelected() {
    this.selectedRider = undefined;
  }

  clear() {
    this?.riderComponent.clear();
    this.finish = undefined;
    this.selectedRider = undefined;
    this.totalTime = undefined;
    this.stopTime = undefined;
    this.value = 5000;
  }

  onReport() {
    const horseCombinationNumberReport = this.selectedRider
      ?.horseCombinationNumber;
    this.selectedRider = undefined;

    const reportedRiderViewDataItem = this.riderViewDataItems.find(
      (r) => r.value.horseCombinationNumber === horseCombinationNumberReport
    );

    this.riderComponent?.clear();
    this.riderViewDataItems = this.riderViewDataItems?.filter(
      (r) =>
        r.value?.horseCombinationNumber !== horseCombinationNumberReport &&
        r.value !== undefined
    );

    const itemId = uuid();
    this.rollingHistListService.unshift(this.histListBucketId, {
      id: itemId,
      key: horseCombinationNumberReport,
      heading: `Ryttarnummer ${horseCombinationNumberReport}`,
      logoSrc: this.logoSrc(reportedRiderViewDataItem.value.clubName),
      h2: `Totaltid ${raceDuration(reportedRiderViewDataItem.value)}`,
      h3: reportedRiderViewDataItem.value.riderName,
    });

    this.postRetryService.enqueue(
      'riderEvent',
      {
        raceGuid: this.userToken.selectedRaceGuid,
        horseCombinationNumber: horseCombinationNumberReport,
        body: this.finishRiderEventPost(this.finish),
      },
      this.histListBucketId,
      itemId
    );
    this.clear();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  riderUnselected() {
    this.selectedRider = undefined;
    this.recalculateFinishDisplay();
  }

  protected onSliderChange($event: Event) {
    const target = $event.target as HTMLInputElement;
    const value = +target.value;
    this.value = value;

    // Beräkna sekund-differensen från grundvärdet 5000
    const secondsDiff = Math.round((value - 5000) / 250);

    // Skapa ny finish-tid med justerade sekunder
    if (this.finish) {
      const adjustedFinish = new Date(
        this.finish.getTime() + secondsDiff * 1000
      );
      this.finish = adjustedFinish;
      this.recalculateFinishDisplay();
    }
  }
}
